import { useTranslations } from "next-intl"

import { ChevronIcon } from "@/components/ui/icons"
import { Link } from "@/i18n/navigation"
import { tv } from "@/lib/utils/tv"

export interface Crumb {
  label: string
  href?: string
}

const breadcrumbs = tv({
  slots: {
    root: "flex items-center",
    item: "flex items-center",
    separator: "",
    link: "hit-target focus-visible:ring-brand rounded-2x transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
    label: "",
    plain: "",
    current: "",
  },
  variants: {
    variant: {
      default: {
        root: "gap-150 font-display text-body-sm text-fg-subtle",
        item: "gap-150",
        separator: "text-fg-muted",
        link: "hover:text-fg",
        current: "text-fg font-semibold",
      },
      inline: {
        root: "font-text text-little @3xl:text-little-lg text-fg-subtle min-w-0 gap-100 font-normal whitespace-nowrap",
        item: "min-w-12 shrink-3 gap-100",
        separator: "text-fg-subtle/50 shrink-0",
        link: "text-fg-subtle hover:text-fg-heading flex min-w-0 underline-offset-2 hover:underline",
        label: "truncate",
        plain: "truncate",
        current: "text-fg-heading truncate font-medium",
      },
    },
    first: { true: "" },
    final: { true: "" },
  },
  compoundVariants: [
    { variant: "inline", final: true, class: { item: "shrink", link: "text-fg-heading font-medium", plain: "text-fg-heading font-medium" } },
    { variant: "inline", first: true, class: { item: "min-w-0 shrink-0" } },
  ],
  defaultVariants: { variant: "default" },
})

export const Breadcrumbs = ({
  items,
  className,
  linkLast = false,
  variant = "default",
}: {
  items: Crumb[]
  className?: string
  linkLast?: boolean
  variant?: "default" | "inline"
}) => {
  const t = useTranslations("breadcrumbs")
  const styles = breadcrumbs({ variant })
  return (
    <nav aria-label={t("ariaLabel")} className={styles.root({ className })}>
      {items.map((item, index) => {
        const final = index === items.length - 1
        const isCurrentPage = final && !linkLast
        return (
          <span key={`${index}:${item.label}`} className={styles.item({ first: index === 0, final })}>
            {index > 0 &&
              (variant === "inline" ? (
                <ChevronIcon aria-hidden size={12} className={styles.separator()} />
              ) : (
                <span aria-hidden className={styles.separator()}>
                  /
                </span>
              ))}
            {item.href && !isCurrentPage ? (
              <Link href={item.href} className={styles.link({ final })}>
                {variant === "inline" ? (
                  <span className={styles.label()} title={item.label}>
                    {item.label}
                  </span>
                ) : (
                  item.label
                )}
              </Link>
            ) : (
              <span
                className={isCurrentPage ? styles.current() : styles.plain({ final })}
                title={variant === "inline" ? item.label : undefined}
                aria-current={isCurrentPage ? "page" : undefined}
              >
                {item.label}
              </span>
            )}
          </span>
        )
      })}
    </nav>
  )
}
